import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormGroup,
	List,
	ListItem,
	ListItemText,
	MenuItem,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ContentStyle from "styles/application/ContentStyle";
import DetailsPanel from "components/Elements/DetailsPanel";
import TabTitle from "components/Elements/TabTitle";
import {
	deleteLocalisationOverride,
	getAvailableLocales,
	getLocaleSettings,
	getLocalisationKeys,
	getLocalisationOverrides,
	upsertLocalisationOverride,
} from "services/localisation";
import { applyLocalisationBundle } from "i18n";
import DownloadIcon from "assets/download.svg";
import { showError, showSuccess } from "../../../../../redux/common/actions";
import {
	ALL_CATEGORIES,
	SHOW_FILTERS,
	TERM_LOCALE,
	buildTerms,
	ccPropertyForKey,
	filterTerms,
	syncCustomCaptionStorage,
} from "./terminologyUtils";
import TermList from "./TermList";
import TermEditor from "./TermEditor";
import CommandPalette from "./CommandPalette";
import BulkEditGrid from "./BulkEditGrid";

const AC = ContentStyle();

const csvEscape = (value) => {
	const s = value == null ? "" : String(value);
	return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Minimal RFC-4180-ish parser: handles quoted fields with embedded commas,
// quotes ("") and newlines.
const parseCsv = (text) => {
	const rows = [];
	let row = [];
	let field = "";
	let inQuotes = false;

	for (let i = 0; i < text.length; i++) {
		const c = text[i];
		if (inQuotes) {
			if (c === '"') {
				if (text[i + 1] === '"') {
					field += '"';
					i++;
				} else {
					inQuotes = false;
				}
			} else {
				field += c;
			}
		} else if (c === '"') {
			inQuotes = true;
		} else if (c === ",") {
			row.push(field);
			field = "";
		} else if (c === "\n") {
			row.push(field);
			rows.push(row);
			row = [];
			field = "";
		} else if (c !== "\r") {
			field += c;
		}
	}
	if (field !== "" || row.length) {
		row.push(field);
		rows.push(row);
	}
	return rows;
};

const localeLabel = (tag) => {
	try {
		const name = new Intl.DisplayNames([tag], { type: "language" }).of(tag);
		return name && name !== tag ? `${name} (${tag})` : tag;
	} catch {
		return tag;
	}
};

const SiteTerminologyContent = ({ state, dispatch, scope: scopeProp }) => {
	const reduxDispatch = useDispatch();
	const { t } = useTranslation();

	// The parents pass a fresh scope object literal on every render. Memoise it
	// from primitives so effects keyed on `scope` (which fetch) don't re-run every
	// render — otherwise the header dispatch below re-renders the parent, which
	// hands us a new scope, which refetches, forever.
	const scopeLevel = scopeProp?.level ?? null;
	const scopeId = scopeProp?.id ?? null;
	const hasScope = !!scopeProp;
	const scope = useMemo(
		() => (hasScope ? { level: scopeLevel, id: scopeId } : undefined),
		[hasScope, scopeLevel, scopeId]
	);

	const showFilterLabels = {
		[SHOW_FILTERS.ALL]: t("option.all"),
		[SHOW_FILTERS.OVERRIDDEN]: t("label.overridden"),
		[SHOW_FILTERS.DEFAULT]: t("label.defaultStatus"),
		[SHOW_FILTERS.RECENT]: t("label.recentlyEdited"),
	};

	const [keys, setKeys] = useState([]);
	const [overrides, setOverrides] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedKey, setSelectedKey] = useState(null);
	const [query, setQuery] = useState("");
	const [category, setCategory] = useState(ALL_CATEGORIES);
	const [show, setShow] = useState(SHOW_FILTERS.ALL);
	const [recentEdits, setRecentEdits] = useState({});
	const [bulkMode, setBulkMode] = useState(false);
	const [paletteOpen, setPaletteOpen] = useState(false);
	// Keys currently being saved. A Set (not a single scalar) so bulk-mode rows can
	// each save concurrently without one in-flight save silently dropping another.
	const [savingKeys, setSavingKeys] = useState(() => new Set());
	const markSaving = useCallback((key, saving) => {
		setSavingKeys((prev) => {
			const next = new Set(prev);
			if (saving) next.add(key);
			else next.delete(key);
			return next;
		});
	}, []);
	const [activeLocale, setActiveLocale] = useState(TERM_LOCALE);
	const [enabledLocales, setEnabledLocales] = useState([TERM_LOCALE]);
	const [importing, setImporting] = useState(false);
	const [preparing, setPreparing] = useState(false);
	const [importOpen, setImportOpen] = useState(false);
	const [importPreview, setImportPreview] = useState(null);
	const [exporting, setExporting] = useState(false);
	const [downloadOpen, setDownloadOpen] = useState(false);
	const [selectedExportLocales, setSelectedExportLocales] = useState([]);
	const fileInputRef = useRef(null);

	const readOnly = !!state?.isReadOnly;
	const appName = state?.details?.data?.application?.name ?? "";

	// The languages this site offers — you edit one language's terminology at a
	// time. Each locale keeps its own set of overrides layered on that language's
	// shipped defaults.
	useEffect(() => {
		let active = true;

		// Site-app scope (or the tenant) edits only the languages that site offers;
		// the application and client scopes can supply any language the build ships.
		const request =
			scope && scope.level !== "siteapp"
				? getAvailableLocales().then((res) =>
						res?.status && res.data?.length ? res.data : null
					)
				: getLocaleSettings(scope).then((res) =>
						res?.status && res.data?.enabledLocales?.length
							? res.data.enabledLocales
							: null
					);

		request.then((locales) => {
			if (active && locales) setEnabledLocales(locales);
		});

		return () => {
			active = false;
		};
	}, [scope]);

	useEffect(() => {
		let active = true;
		setLoading(true);

		(async () => {
			const [keysRes, overridesRes] = await Promise.all([
				getLocalisationKeys(activeLocale),
				getLocalisationOverrides(activeLocale, scope),
			]);

			if (!active) return;

			if (keysRes?.status && overridesRes?.status) {
				setKeys(keysRes.data ?? []);
				setOverrides(overridesRes.data ?? []);
				setSelectedKey(
					(current) => current ?? keysRes.data?.[0]?.key ?? null
				);
			} else {
				reduxDispatch(showError(t("message.failedToLoadSiteTerminology")));
			}

			setLoading(false);
		})();

		return () => {
			active = false;
		};
	}, [reduxDispatch, activeLocale, scope, t]);

	// Cmd/Ctrl+K opens the jump palette from anywhere on the screen.
	useEffect(() => {
		const onKeyDown = (e) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				setPaletteOpen(true);
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);

	const terms = useMemo(() => buildTerms(keys, overrides), [keys, overrides]);

	const categories = useMemo(
		() => [...new Set(terms.map((t) => t.category))].sort(),
		[terms]
	);

	const filteredTerms = useMemo(
		() =>
			filterTerms(terms, {
				query,
				category,
				show,
				recentKeys: recentEdits,
			}),
		[terms, query, category, show, recentEdits]
	);

	const selectedTerm = terms.find((t) => t.key === selectedKey) ?? null;

	// After a successful save/reset the server returns the rebuilt bundle:
	// hot-swap it into i18next, refresh the local override rows, and keep the
	// legacy me.customCaptions blob + site-app context *CC fields in step so
	// every existing consumer sees the change without a reload.
	const applyServerResult = useCallback(
		(term, forms, bundle) => {
			// Only the site-app scope returns (and hot-swaps) the live bundle; the
			// application/client scopes return the override list, which no live UI
			// consumes here.
			if (!scope) applyLocalisationBundle(bundle);

			setOverrides((current) => [
				...current.filter((o) => o.key !== term.key),
				...Object.entries(forms ?? {})
					.filter(([, value]) => value !== null && value !== "")
					.map(([pluralCategory, value]) => ({
						key: term.key,
						locale: activeLocale,
						pluralCategory: pluralCategory === "" ? null : pluralCategory,
						value,
					})),
			]);

			setRecentEdits((current) => ({
				...current,
				[term.key]: Date.now(),
			}));

			// The legacy me.customCaptions blob and the site-app context *CC fields
			// are English-only and specific to the current site app; only mirror
			// there when editing the English set of THIS site app (no scope).
			if (scope || activeLocale !== "en") return;

			const isReset = forms === null;
			const singular = isReset
				? term.defaultSingular
				: forms.one ?? forms[""] ?? term.defaultSingular;
			const plural = isReset
				? term.defaultPlural
				: forms.other ?? term.defaultPlural;

			syncCustomCaptionStorage(term, { singular, plural });

			const prop = ccPropertyForKey(term.key);
			if (prop && state?.details?.data) {
				dispatch({
					type: "SET_SITE_APP_DETAIL",
					payload: {
						...state.details,
						data: {
							...state.details.data,
							[`${prop}CC`]: isReset ? null : singular,
							...(term.hasPlural
								? { [`${prop}PluralCC`]: isReset ? null : plural }
								: {}),
						},
					},
				});
			}
		},
		[dispatch, state?.details, activeLocale, scope]
	);

	const handleSave = useCallback(
		async (term, { singular, plural }) => {
			// Block only a concurrent save of the SAME key, so bulk-editing several
			// rows in quick succession never drops a row's save.
			if (!term || savingKeys.has(term.key)) return false;

			if (
				singular === "" ||
				(term.hasPlural && plural === "")
			) {
				reduxDispatch(
					showError(t("message.terminologyValuesCannotBeEmpty"))
				);
				return false;
			}

			const forms = term.hasPlural
				? { one: singular, other: plural }
				: { "": singular };

			markSaving(term.key, true);
			const response = await upsertLocalisationOverride({
				key: term.key,
				locale: activeLocale,
				forms,
				scope,
			});
			markSaving(term.key, false);

			if (!response?.status) {
				reduxDispatch(
					showError(
						typeof response?.data === "string"
							? response.data
							: t("message.failedToSaveTerminology")
					)
				);
				return false;
			}

			applyServerResult(term, forms, response.data);
			reduxDispatch(
				showSuccess(t("message.termSaved", { label: term.label }))
			);
			return true;
		},
		[applyServerResult, reduxDispatch, savingKeys, markSaving, activeLocale, scope, t]
	);

	const handleReset = useCallback(
		async (term) => {
			if (!term || savingKeys.has(term.key)) return;

			markSaving(term.key, true);
			const response = await deleteLocalisationOverride(
				term.key,
				activeLocale,
				scope
			);
			markSaving(term.key, false);

			if (!response?.status) {
				reduxDispatch(showError(t("message.failedToResetTerminology")));
				return;
			}

			applyServerResult(term, null, response.data);
			reduxDispatch(
				showSuccess(t("message.termResetToDefault", { label: term.label }))
			);
		},
		[applyServerResult, reduxDispatch, savingKeys, markSaving, activeLocale, scope, t]
	);

	const handleSaveAndNext = useCallback(
		async (term, values) => {
			const saved = await handleSave(term, values);
			if (!saved) return;

			const index = filteredTerms.findIndex((t) => t.key === term.key);
			const next = filteredTerms[index + 1];
			if (next) setSelectedKey(next.key);
		},
		[filteredTerms, handleSave]
	);

	// Fetch a locale's shipped defaults + overrides and index them by key so we can
	// read/compare each language's effective value during multi-language export
	// and import.
	const fetchLocaleTerms = useCallback(
		async (locale) => {
			const [keysRes, overridesRes] = await Promise.all([
				getLocalisationKeys(locale),
				getLocalisationOverrides(locale, scope),
			]);
			if (!keysRes?.status || !overridesRes?.status) return null;
			const built = buildTerms(keysRes.data ?? [], overridesRes.data ?? []);
			return new Map(built.map((term) => [term.key, term]));
		},
		[scope]
	);

	// One CSV can carry several languages: a Key + Term column, then a
	// singular/plural pair per selected language. The value written is that
	// language's current (effective) value, so an untranslated locale exports its
	// shipped default as the editable starting point.
	const exportLocales = useCallback(
		async (locales) => {
			if (!locales.length || exporting) return;
			setExporting(true);
			try {
				const maps = await Promise.all(locales.map(fetchLocaleTerms));
				const localeMaps = locales
					.map((locale, i) => [locale, maps[i]])
					.filter(([, map]) => map);
				if (!localeMaps.length) {
					reduxDispatch(showError(t("message.failedToLoadSiteTerminology")));
					return;
				}

				const header = ["Key", "Term"];
				localeMaps.forEach(([locale]) => {
					header.push(locale, `${locale} (plural)`);
				});

				const rows = terms.map((term) => {
					const row = [term.key, term.label];
					localeMaps.forEach(([, map]) => {
						const localeTerm = map.get(term.key);
						row.push(localeTerm?.effectiveSingular ?? "");
						row.push(
							localeTerm?.hasPlural
								? localeTerm.effectivePlural ?? ""
								: ""
						);
					});
					return row;
				});

				const csv = [header, ...rows]
					.map((row) => row.map(csvEscape).join(","))
					.join("\r\n");
				// The BOM makes Excel read the UTF-8 payload (e.g. Chinese) correctly.
				const blob = new Blob([`﻿${csv}`], {
					type: "text/csv;charset=utf-8;",
				});
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				const localeTag =
					localeMaps.length === 1 ? `-${localeMaps[0][0]}` : "";
				link.href = url;
				link.download = `terminology-${scope?.level ?? "siteapp"}-${
					scope?.id ?? ""
				}${localeTag}.csv`;
				document.body.appendChild(link);
				link.click();
				link.remove();
				URL.revokeObjectURL(url);
			} finally {
				setExporting(false);
			}
		},
		[terms, scope, exporting, fetchLocaleTerms, reduxDispatch, t]
	);

	// A single language downloads straight away; with more than one available we
	// let the user tick which languages to include first.
	const openExport = useCallback(() => {
		if (enabledLocales.length <= 1) {
			exportLocales(enabledLocales);
			return;
		}
		setSelectedExportLocales(enabledLocales);
		setDownloadOpen(true);
	}, [enabledLocales, exportLocales]);

	// Building the preview does everything the import does except the writes: parse
	// the file, resolve each language column, and collect the cells that would
	// actually change — split into brand-new overrides vs. ones that replace an
	// existing override — so the user can review before committing.
	const prepareImport = useCallback(
		async (file) => {
			if (!file || importing || preparing) return;
			setPreparing(true);
			try {
				const rows = parseCsv(await file.text()).filter((row) =>
					row.some((cell) => cell.trim() !== "")
				);
				if (rows.length < 2) {
					reduxDispatch(showError(t("message.noMatchingTerminologyInCsv")));
					return;
				}

				const header = rows[0].map((h) => h.trim().toLowerCase());
				const keyIdx = header.indexOf("key");
				// Each language the site offers may appear as its own column pair; we
				// import whichever languages the file actually contains.
				const localeColumns = enabledLocales
					.map((locale) => ({
						locale,
						singularIdx: header.indexOf(locale.toLowerCase()),
						pluralIdx: header.indexOf(`${locale.toLowerCase()} (plural)`),
					}))
					.filter((column) => column.singularIdx >= 0);

				if (keyIdx < 0 || !localeColumns.length) {
					reduxDispatch(
						showError(t("message.terminologyCsvMissingColumns"))
					);
					return;
				}

				// Load each imported language's current values so we skip unchanged
				// cells and honour its plural shape (which differs per language).
				const localeTermMaps = new Map();
				await Promise.all(
					localeColumns.map(async ({ locale }) => {
						localeTermMaps.set(locale, await fetchLocaleTerms(locale));
					})
				);

				const changes = [];
				for (const row of rows.slice(1)) {
					const key = (row[keyIdx] ?? "").trim();
					if (!key) continue;

					for (const column of localeColumns) {
						const localeTerm = localeTermMaps
							.get(column.locale)
							?.get(key);
						if (!localeTerm) continue;

						const singular = (row[column.singularIdx] ?? "").trim();
						const plural =
							column.pluralIdx >= 0
								? (row[column.pluralIdx] ?? "").trim()
								: "";

						// Values can't be stored empty, so a blank singular is skipped.
						if (!singular) continue;

						// Only surface cells that change the current value; an untouched
						// export is a no-op.
						if (
							singular === localeTerm.effectiveSingular &&
							(!localeTerm.hasPlural ||
								plural === localeTerm.effectivePlural)
						) {
							continue;
						}

						const newPlural = localeTerm.hasPlural
							? plural || localeTerm.effectivePlural
							: "";
						changes.push({
							key,
							label: localeTerm.label,
							locale: column.locale,
							hasPlural: localeTerm.hasPlural,
							oldSingular: localeTerm.effectiveSingular,
							oldPlural: localeTerm.effectivePlural,
							newSingular: singular,
							newPlural,
							// An existing override being changed = overwrite; otherwise
							// it's a brand-new override on top of the shipped default.
							isOverwrite: localeTerm.isOverridden,
							forms: localeTerm.hasPlural
								? { one: singular, other: newPlural }
								: { "": singular },
						});
					}
				}

				if (!changes.length) {
					reduxDispatch(showSuccess(t("message.nothingToImport")));
					return;
				}

				setImportPreview({
					changes,
					newCount: changes.filter((c) => !c.isOverwrite).length,
					overwriteCount: changes.filter((c) => c.isOverwrite).length,
				});
				setImportOpen(true);
			} finally {
				setPreparing(false);
			}
		},
		[
			enabledLocales,
			importing,
			preparing,
			fetchLocaleTerms,
			reduxDispatch,
			t,
		]
	);

	// The preview already resolved every write, so confirming just replays them.
	const confirmImport = useCallback(async () => {
		if (!importPreview || importing) return;
		setImporting(true);
		try {
			let saved = 0;
			let failed = 0;
			const touched = new Set();

			for (const change of importPreview.changes) {
				// eslint-disable-next-line no-await-in-loop
				const response = await upsertLocalisationOverride({
					key: change.key,
					locale: change.locale,
					forms: change.forms,
					scope,
				});
				if (response?.status) {
					saved++;
					touched.add(change.locale);
				} else {
					failed++;
				}
			}

			// Refresh the on-screen language if it was among those imported.
			if (touched.has(activeLocale)) {
				const refreshed = await getLocalisationOverrides(activeLocale, scope);
				if (refreshed?.status) setOverrides(refreshed.data ?? []);
			}

			if (failed) {
				reduxDispatch(showError(t("message.failedToImport")));
			} else {
				reduxDispatch(
					showSuccess(t("message.terminologyImported", { count: saved }))
				);
			}
			setImportOpen(false);
			setImportPreview(null);
		} finally {
			setImporting(false);
		}
	}, [importPreview, importing, scope, activeLocale, reduxDispatch, t]);

	// At the site-app (and legacy tenant) scope this screen renders inside the same
	// SiteApplication header as the Translation Glossary, so surface Download +
	// Import there for an identical UI. The application/client editors have no such
	// header, so they fall back to the inline buttons rendered in the toolbar.
	const inSiteAppHeader = !scope || scope.level === "siteapp";

	useEffect(() => {
		if (!inSiteAppHeader) return undefined;

		dispatch({
			type: "SET_HEADER_IMPORT",
			payload: {
				visible: !readOnly,
				label: t("action.importFromCsv"),
				onClick: () => fileInputRef.current?.click(),
			},
		});
		if (terms.length > 0) {
			dispatch({
				type: "SET_HEADER_ICON_ACTION",
				payload: {
					iconSrc: DownloadIcon,
					title: t("action.downloadCsv"),
					isLoading: exporting,
					loadingNode: <CircularProgress size={24} />,
					onClick: openExport,
				},
			});
		} else {
			dispatch({ type: "CLEAR_HEADER_ICON_ACTION" });
		}

		return () => {
			dispatch({ type: "CLEAR_HEADER_IMPORT" });
			dispatch({ type: "CLEAR_HEADER_ICON_ACTION" });
		};
	}, [
		inSiteAppHeader,
		dispatch,
		readOnly,
		terms.length,
		exporting,
		openExport,
		t,
	]);

	return (
		<div>
			<TabTitle
				title={
					appName
						? t("title.appSiteTerminology", { appName })
						: t("title.siteTerminology")
				}
			/>

			<CommandPalette
				open={paletteOpen}
				onClose={() => setPaletteOpen(false)}
				terms={terms}
				onPick={setSelectedKey}
			/>

			<Dialog
				open={downloadOpen}
				onClose={() => !exporting && setDownloadOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{t("title.selectLanguagesToDownload")}</DialogTitle>
				<DialogContent>
					<FormGroup>
						{enabledLocales.map((tag) => (
							<FormControlLabel
								key={tag}
								control={
									<Checkbox
										checked={selectedExportLocales.includes(tag)}
										onChange={(e) =>
											setSelectedExportLocales((current) =>
												e.target.checked
													? [...current, tag]
													: current.filter((l) => l !== tag)
											)
										}
									/>
								}
								label={localeLabel(tag)}
							/>
						))}
					</FormGroup>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDownloadOpen(false)}
						disabled={exporting}
					>
						{t("action.cancel")}
					</Button>
					<Button
						variant="contained"
						disabled={!selectedExportLocales.length || exporting}
						startIcon={
							exporting ? <CircularProgress size={16} /> : undefined
						}
						onClick={async () => {
							// Keep the site's language order in the file.
							const ordered = enabledLocales.filter((l) =>
								selectedExportLocales.includes(l)
							);
							await exportLocales(ordered);
							setDownloadOpen(false);
						}}
					>
						{t("action.downloadCsv")}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={importOpen}
				onClose={() => !importing && setImportOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>{t("title.importPreview")}</DialogTitle>
				<DialogContent dividers>
					{importPreview && (
						<>
							<Alert severity="info" sx={{ mb: 2 }}>
								{t("message.importPreviewSummary", {
									newCount: importPreview.newCount,
									replacedCount: importPreview.overwriteCount,
									importedCount: importPreview.changes.length,
								})}
							</Alert>

							{importPreview.newCount > 0 && (
								<>
									<Typography variant="subtitle2" sx={{ mb: 1 }}>
										{t("label.newRecordsCount", {
											count: importPreview.newCount,
										})}
									</Typography>
									<List dense>
										{importPreview.changes
											.filter((c) => !c.isOverwrite)
											.slice(0, 20)
											.map((c, i) => (
												<ListItem
													key={`new-${c.locale}-${c.key}-${i}`}
													disableGutters
												>
													<ListItemText
														primary={`${c.label} (${localeLabel(
															c.locale
														)})`}
														secondary={
															c.hasPlural
																? `${c.newSingular} / ${c.newPlural}`
																: c.newSingular
														}
													/>
												</ListItem>
											))}
									</List>
								</>
							)}

							{importPreview.overwriteCount > 0 && (
								<>
									<Typography
										variant="subtitle2"
										sx={{
											mb: 1,
											mt: importPreview.newCount > 0 ? 2 : 0,
										}}
									>
										{t("label.replacedRecordsCount", {
											count: importPreview.overwriteCount,
										})}
									</Typography>
									<List dense>
										{importPreview.changes
											.filter((c) => c.isOverwrite)
											.slice(0, 20)
											.map((c, i) => (
												<ListItem
													key={`ovr-${c.locale}-${c.key}-${i}`}
													disableGutters
												>
													<ListItemText
														primary={`${c.label} (${localeLabel(
															c.locale
														)})`}
														secondary={`${c.oldSingular} → ${c.newSingular}`}
													/>
												</ListItem>
											))}
									</List>
								</>
							)}

							{importPreview.changes.length > 20 && (
								<Typography variant="caption" color="text.secondary">
									{t("message.showingFirst20Preview")}
								</Typography>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setImportOpen(false)}
						disabled={importing}
					>
						{t("action.cancel")}
					</Button>
					<Button
						variant="contained"
						onClick={confirmImport}
						disabled={importing}
						startIcon={
							importing ? <CircularProgress size={16} /> : undefined
						}
					>
						{t("action.confirmImport")}
					</Button>
				</DialogActions>
			</Dialog>

			<div className="detailsContainer">
				<DetailsPanel
					header={t("title.siteTerminology")}
					dataCount={terms.length}
					description={t("message.siteTerminologyDescription")}
				/>
			</div>

			<Box
				sx={{
					display: "flex",
					gap: 2,
					alignItems: "center",
					flexWrap: "wrap",
					mb: 2,
				}}
			>
				<TextField
					select
					label={t("label.language")}
					size="small"
					value={activeLocale}
					onChange={(e) => setActiveLocale(e.target.value)}
					sx={{ minWidth: 160 }}
				>
					{enabledLocales.map((tag) => (
						<MenuItem key={tag} value={tag}>
							{localeLabel(tag)}
						</MenuItem>
					))}
				</TextField>

				<TextField
					select
					label={t("label.category")}
					size="small"
					value={category}
					onChange={(e) => setCategory(e.target.value)}
					sx={{ minWidth: 180 }}
				>
					<MenuItem value={ALL_CATEGORIES}>{t("option.all")}</MenuItem>
					{categories.map((c) => (
						<MenuItem key={c} value={c}>
							{c}
						</MenuItem>
					))}
				</TextField>

				<TextField
					select
					label={t("label.show")}
					size="small"
					value={show}
					onChange={(e) => setShow(e.target.value)}
					sx={{ minWidth: 180 }}
				>
					{Object.values(SHOW_FILTERS).map((option) => (
						<MenuItem key={option} value={option}>
							{showFilterLabels[option] ?? option}
						</MenuItem>
					))}
				</TextField>

				<input
					ref={fileInputRef}
					type="file"
					accept=".csv,text/csv"
					style={{ display: "none" }}
					onChange={(e) => {
						const file = e.target.files?.[0];
						if (file) prepareImport(file);
						// Reset so re-selecting the same file fires onChange again.
						e.target.value = "";
					}}
				/>

				{!inSiteAppHeader && (
					<>
						<Button
							variant="outlined"
							size="small"
							startIcon={
								exporting ? (
									<CircularProgress size={16} />
								) : (
									<img
										src={DownloadIcon}
										alt=""
										width={16}
										height={16}
									/>
								)
							}
							onClick={openExport}
							disabled={loading || exporting || !terms.length}
						>
							{t("action.downloadCsv")}
						</Button>

						<Button
							variant="outlined"
							size="small"
							onClick={() => fileInputRef.current?.click()}
							disabled={loading || readOnly || preparing || importing}
							startIcon={
								preparing ? (
									<CircularProgress size={16} />
								) : undefined
							}
						>
							{t("action.importFromCsv")}
						</Button>
					</>
				)}

				<Box sx={{ flexGrow: 1 }} />

				<FormControlLabel
					control={
						<Switch
							checked={bulkMode}
							onChange={(e) => setBulkMode(e.target.checked)}
						/>
					}
					label={t("label.bulkEdit")}
				/>
			</Box>

			{loading ? (
				<AC.SpinnerContainer>
					<CircularProgress />
				</AC.SpinnerContainer>
			) : bulkMode ? (
				<BulkEditGrid
					terms={filteredTerms}
					readOnly={readOnly}
					savingKeys={savingKeys}
					onSaveRow={handleSave}
				/>
			) : (
				<Box
					sx={{
						display: "flex",
						gap: 3,
						alignItems: "stretch",
						minHeight: 420,
					}}
				>
					<Box sx={{ width: 360, flexShrink: 0 }}>
						<TermList
							terms={filteredTerms}
							selectedKey={selectedKey}
							onSelect={setSelectedKey}
							query={query}
							onQueryChange={setQuery}
						/>
					</Box>

					<Box
						sx={{
							flexGrow: 1,
							minWidth: 0,
							// Keep the editor in view if the page does scroll
							// (small screens where the list cap can't help).
							position: "sticky",
							top: 16,
							alignSelf: "flex-start",
						}}
					>
						<TermEditor
							term={selectedTerm}
							readOnly={readOnly}
							saving={savingKeys.has(selectedTerm?.key)}
							onSave={(values) => handleSave(selectedTerm, values)}
							onSaveAndNext={(values) =>
								handleSaveAndNext(selectedTerm, values)
							}
							onReset={() => handleReset(selectedTerm)}
							lastEditedAt={recentEdits[selectedTerm?.key]}
						/>
					</Box>
				</Box>
			)}
		</div>
	);
};

export default SiteTerminologyContent;
